import React, { FC, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useSearch } from '../../hooks/useSearch';
import { SearchCategory, TypeOfSearch } from '../../types/enums';
import { CurrencyCodeType, getCurrencyName } from '../../utils/getCurrencyName';
import { getOptions } from '../../utils/getOptions';
import { Button } from '../Button';
import { AltSelect } from '../forms/AltSelect';
import { LocationSearch } from './LocationSearch';
import { usePriceOptions } from '../../hooks/usePriceOptions';

interface AudienceSearchProps {
  category: SearchCategory;
}

export interface Option {
  value: number;
  name: string;
  selected?: boolean;
}

const StyledSpacer = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.sm};

    @media (min-width: ${media.lg}) {
      display: flex;
      flex-direction: row;
    }
  `}
`;
const StyledAudienceSearch = styled.div`
  position: relative;
`;

const StyledLocationSearch = styled(LocationSearch)`
  ${({ theme: { space, media } }) => css`
    @media (min-width: ${media.xl}) {
      min-width: 408px;
    }
  `}
`;

const StyledInlineFilters = styled.div`
  ${({ theme: { space, media, maxMedia } }) => css`
    display: grid;
    gap: ${space.sm};

    @media (max-width: ${maxMedia.md}) {
      grid-template-columns: 1fr 1fr;
      > *:last-child {
        grid-column: 1 / 3;
      }
    }

    @media (min-width: ${media.md}) {
      flex-grow: 1;
      grid-template-columns: 1fr 1fr 1fr;
    }
  `}
`;

export const AudienceSearch: FC<AudienceSearchProps> = ({ category }) => {
  const { searchMeta, handleOnSubmit, formMethods } = useSearch({
    overrideValues: { type: TypeOfSearch.Residential },
    activeView: 'list',
    disableAutoSearch: true,
  });

  const { current } = useCurrencies();
  const currencySaleType = category.toLowerCase();
  const currencyName = getCurrencyName(current?.code as CurrencyCodeType);
  const initialMinPriceOptions: Option[] = searchMeta?.[`${currencySaleType}LowestPrice${currencyName}`];
  const initialMaxPriceOptions: Option[] = searchMeta?.[`${currencySaleType}HighestPrice${currencyName}`];

  const { minPriceOptions, maxPriceOptions } = usePriceOptions({
    initialMinPriceOptions,
    initialMaxPriceOptions,
    formMethods,
  });

  const { register, setValue } = formMethods;

  useEffect(() => {
    setValue('category', category);
  }, [category, setValue]);

  return (
    <StyledAudienceSearch>
      <FormProvider {...formMethods}>
        <StyledSpacer spacing="half">
          <StyledLocationSearch category={category} placeholder="Search by location, postcode or station" />
          <StyledInlineFilters>
            <AltSelect
              id="priceLowest"
              label="Min price"
              {...register('priceLowest', {
                valueAsNumber: true,
              })}
              options={getOptions(minPriceOptions)}
              constrained
            />
            <AltSelect
              id="priceHighest"
              label="Max price"
              {...register('priceHighest', {
                valueAsNumber: true,
              })}
              options={getOptions(maxPriceOptions)}
              constrained
            />

            <AltSelect
              id="bedrooms"
              label="Bedrooms"
              {...register('bedrooms', {
                valueAsNumber: false,
              })}
              options={[{ value: '', name: 'All' }, ...getOptions(searchMeta?.bedroomsMinimum)]}
              constrained
            />
          </StyledInlineFilters>
          <Button onClick={handleOnSubmit} type="button" disableClickTracking>
            Search
          </Button>
        </StyledSpacer>
      </FormProvider>
    </StyledAudienceSearch>
  );
};
